import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
} from "@mui/material";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DateTimePicker } from "@mui/x-date-pickers";
import { X } from "lucide-react";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { createPortal } from "react-dom";

import { Event } from "0.Mine/3.Interfaces/Event";
import { UserLicense } from "0.Mine/3.Interfaces/license";

import ClientQuery from "../ClientQuery";

// Simulated API call for assignment
async function assignEventToUser(userId: string | number, event: Event): Promise<boolean> {
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log(`Assigned event to user ${userId}:`, event);
      resolve(true);
    }, 1000);
  });
}

interface EventFormUIProps {
  event: Event;
  isEdit: boolean;
  onSave: (event: Event) => void;
  onCancel: () => void;
  formData: Event;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleStartDateChange: (date: Date | null) => void;
  handleEndDateChange: (date: Date | null) => void;
  handleCheckboxChange: (name: string, checked: boolean) => void;
  handleSubmit: (e: React.FormEvent) => void;
  styles: Record<string, any>;
  users: UserLicense[];
}

export const EventFormUI: React.FC<EventFormUIProps> = ({
  isEdit,
  onCancel,
  formData,
  handleChange,
  handleStartDateChange,
  handleEndDateChange,
  handleCheckboxChange,
  handleSubmit,
  styles,
  users,
}) => {
  const [sharedEmails, setSharedEmails] = useState<string[]>(formData.sharedWith || []);

  const [assignedUsers, setAssignedUsers] = useState<UserLicense[]>(() => {
    if (formData.vendorId && formData.vendorId.length > 0) {
      return users.filter((user) => formData.vendorId.includes(Number(user.id)));
    }
    return [];
  });

  const [assignedUserId, setAssignedUserId] = useState<string | number>("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Guest inputs
  const [guestEmail, setGuestEmail] = useState<string>("");
  const [guestEmailError, setGuestEmailError] = useState<string | null>(null);

  // Side panel toggle
  const [showSideColumn, setShowSideColumn] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  // Helpers
  const getUserDisplayName = (user: UserLicense) =>
    user.name || user.username || user.email || `User ${user.id}`;

  const handleAssignChange = (event: SelectChangeEvent<string | number>) => {
    setAssignedUserId(event.target.value);
  };

  const handleAssignToUser = async () => {
    if (!assignedUserId) return;
    setIsAssigning(true);
    try {
      const user = users.find((u) => u.id === assignedUserId);
      if (!user) throw new Error("User not found");

      // Update the formData to include the vendor ID
      formData.vendorId = formData.vendorId || [];
      if (!formData.vendorId.includes(Number(assignedUserId))) {
        formData.vendorId.push(Number(assignedUserId));
      }

      // Add to shared emails if not already included
      if (user.email && !sharedEmails.includes(user.email)) {
        const updated = [...sharedEmails, user.email];
        setSharedEmails(updated);
        formData.sharedWith = updated;
      }

      // Add to assigned users list for UI
      if (!assignedUsers.some((u) => u.id === user.id)) {
        setAssignedUsers((prev) => [...prev, user]);
      }

      enqueueSnackbar(`Evento assegnato a ${getUserDisplayName(user)}`, { variant: "success" });
      setAssignedUserId("");
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Errore nell'assegnazione dell'evento", { variant: "error" });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveAssignedUser = (userId: string | number) => {
    setAssignedUsers((prev) => prev.filter((u) => u.id !== userId));

    // Remove from vendorId array
    formData.vendorId = formData.vendorId.filter((id) => id !== Number(userId));

    // Remove from shared emails if exists
    const user = users.find((u) => u.id === userId);
    if (user?.email) {
      const updated = sharedEmails.filter((email) => email !== user.email);
      setSharedEmails(updated);
      formData.sharedWith = updated;
    }
  };

  const handleAddGuest = () => {
    if (!guestEmail) {
      setGuestEmailError("Inserisci un'email");
      return;
    }
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(guestEmail)) {
      setGuestEmailError("Email non valida");
      return;
    }
    if (sharedEmails.includes(guestEmail)) {
      setGuestEmailError("Ospite già aggiunto");
      return;
    }
    const updated = [...sharedEmails, guestEmail];
    setSharedEmails(updated);
    formData.sharedWith = updated;
    setGuestEmail("");
    setGuestEmailError(null);
  };

  const handleGuestEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuestEmail(e.target.value);
    setGuestEmailError(null);
  };

  const handleGuestEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddGuest();
    }
  };

  return createPortal(
    <Box sx={styles.overlay}>
      <Box sx={styles.container} component="form" onSubmit={handleSubmit}>
        {/* Header */}
        <Box
          sx={{
            ...styles.header,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "0px!important",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="h6" sx={{ color: "white!important", paddingTop: "5px" }}>
              {isEdit ? "Modifica Evento" : "Aggiungi Evento"}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowSideColumn((prev) => !prev)}
              sx={{ color: "white!important", borderColor: "white!important", marginLeft: "10px" }}
            >
              {showSideColumn ? "Nascondi Dati" : "Mostra Dati"}
            </Button>
          </Box>
          <IconButton
            onClick={onCancel}
            sx={{ color: "white!important", fill: "#ffffff!important" }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ display: "flex", width: "100%" }}>
          {/* Main form */}
          <Box sx={{ flex: 1, ...styles.content }}>
            {/* Title */}
            <Box sx={styles.formGroup}>
              <Typography sx={styles.label}>Titolo</Typography>
              <TextField
                fullWidth
                name="title"
                placeholder="Nome evento"
                value={formData.title}
                onChange={handleChange}
                sx={styles.textField}
                autoFocus
              />
            </Box>

            {/* Dates */}
            <Box sx={styles.gridTwoColumns}>
              <Box sx={styles.dateTimeWrapper}>
                <Typography sx={styles.label}>Inizio</Typography>
                <DateTimePicker
                  value={formData.start}
                  onChange={handleStartDateChange}
                  ampm={false}
                  format="dd/MM/yyyy HH:mm"
                  slotProps={{
                    popper: { sx: { zIndex: 1300, border: "0" } },
                    textField: { fullWidth: true, sx: styles.dateTimePicker },
                  }}
                />
              </Box>
              <Box sx={styles.dateTimeWrapper}>
                <Typography sx={styles.label}>Fine</Typography>
                <DateTimePicker
                  value={formData.end}
                  onChange={handleEndDateChange}
                  ampm={false}
                  format="dd/MM/yyyy HH:mm"
                  minDateTime={formData.start || undefined}
                  slotProps={{
                    popper: { sx: { zIndex: 1300, border: "0" } },
                    textField: {
                      fullWidth: true,
                      sx: styles.dateTimePicker,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Location */}
            <Box sx={styles.formGroup}>
              <Typography sx={styles.label}>Location</Typography>
              <TextField
                fullWidth
                name="location"
                placeholder="Luogo dell'evento"
                value={formData.location}
                onChange={handleChange}
                sx={styles.textField}
              />
            </Box>

            {/* Description */}
            <Box sx={styles.formGroup}>
              <Typography sx={styles.label}>Descrizione</Typography>
              <TextField
                fullWidth
                name="description"
                placeholder="Descrizione evento"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                sx={{
                  ...styles.textField,
                  "& textarea": { width: "100%", boxSizing: "border-box" },
                }}
              />
            </Box>

            {/* Assignment */}
            <Box sx={styles.formGroup}>
              <Typography sx={styles.label}>Assegna a</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="assign-user-label"
                    sx={{ lineHeight: "0.81em", fontSize: "14px", padding: "0px 5px" }}
                  >
                    Utente
                  </InputLabel>
                  <Select
                    labelId="assign-user-label"
                    value={assignedUserId}
                    onChange={handleAssignChange}
                    disabled={isAssigning}
                  >
                    <MenuItem value="" disabled>
                      <em>Seleziona</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem
                        key={u.id}
                        value={u.id}
                        disabled={assignedUsers.some((a) => a.id === u.id)}
                      >
                        {getUserDisplayName(u)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleAssignToUser}
                  disabled={!assignedUserId || isAssigning}
                >
                  Assegna
                </Button>
              </Box>
              {assignedUsers.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {assignedUsers.map((u) => (
                    <Chip
                      key={u.id}
                      label={getUserDisplayName(u)}
                      onDelete={() => handleRemoveAssignedUser(u.id)}
                      icon={<CheckCircleIcon />}
                      color="primary"
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Guests */}
            <Box sx={{ margin: "20px 0" }}>
              <Typography sx={styles.label}>Ospiti</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder="email@example.com"
                  value={guestEmail}
                  onChange={handleGuestEmailChange}
                  onKeyPress={handleGuestEmailKeyPress}
                  error={Boolean(guestEmailError)}
                  helperText={guestEmailError}
                />
                <Button onClick={handleAddGuest} variant="contained">
                  Aggiungi
                </Button>
              </Box>
              {sharedEmails.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {sharedEmails.map((e, i) => (
                    <Chip
                      key={i}
                      label={e}
                      onDelete={() => {
                        const upd = sharedEmails.filter((se) => se !== e);
                        setSharedEmails(upd);
                        formData.sharedWith = upd;
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Reminders 
                        <Box sx={styles.switchContainer}>
                            <Switch
                                checked={formData.remindOnWhatsapp}
                                onChange={(e) => handleCheckboxChange('remindOnWhatsapp', e.target.checked)}
                            />
                            <Typography sx={styles.switchLabel}>
                                Promemoria WhatsApp
                            </Typography>
                        </Box>*/}
            <Box sx={styles.switchContainer}>
              <Switch
                checked={formData.remindViaEmail}
                onChange={(e) => handleCheckboxChange("remindViaEmail", e.target.checked)}
              />
              <Typography sx={styles.switchLabel}>Promemoria Email</Typography>
            </Box>
          </Box>

          {/* Side panel */}
          <Box
            sx={{
              width: showSideColumn ? 350 : 0,
              transition: "width 0.3s",
              overflow: "hidden",
              background: "#f0f0f0",
              borderRadius: "0px!important",
            }}
          >
            {showSideColumn && (
              <Box sx={{ p: 2, borderRadius: "0px!important" }}>
                <Typography variant="h5">Dati Aggiuntivi</Typography>
                <ClientQuery />
              </Box>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={styles.buttonContainer}>
          <Button onClick={onCancel}>Annulla</Button>
          <Button type="submit" variant="contained">
            {isEdit ? "Aggiorna" : "Crea"}
          </Button>
        </Box>
      </Box>
    </Box>,
    document.body
  );
};
